@if (label()) {
  <label
    class="mb-x-sm block text-neutral-900 font-medium"
    [class.cursor-not-allowed]="disabled()"
    [class.text-neutral-300]="disabled()"
    [class.cursor-pointer]="!disabled()"
    [class.label-required]="required()"
    (click)="!disabled() && toggleDropdown()"
    [for]="id()">
    {{ label() }}
  </label>
}

<!-- Select Container -->
<div class="relative" libClickOutSide (clickOutside)="clickedOutside()">
  @let controlHasErrors =
    childControl.touched && childControl.dirty && childControl.errors;
  <label
    (click)="!disabled() && toggleDropdown()"
    class="select flex items-center w-full text-xs font-medium"
    [class.cursor-not-allowed]="disabled()"
    [class.text-neutral-300]="disabled()"
    [class.cursor-pointer]="!disabled()"
    [class.focus]="isDropdownOpen"
    [class.text-neutral-400]="!disabled() && !controlHasErrors"
    [class.has-errors]="controlHasErrors"
    [class.text-destructive-500]="controlHasErrors"
    [class]="inputClass()">
    <input
      type="hidden"
      [id]="id()"
      [readOnly]="readonly()"
      [formControlName]="inputFormControlName()"
      [required]="required()"
      [value]="value() ?? null"
      [placeholder]="placeholder()"
      class="hidden-input" />

    @if (selectedOption && selectedOption['label']) {
      <div class="flex justify-between items-center w-full">
        <div class="flex gap-sm items-center">
          <label
            class="pointer"
            [style.cursor]="disabled() ? 'not-allowed' : ''"
            (click)="!disabled() && toggleDropdown()">
            <lib-avatar
              [fullName]="
                selectedOption[selectedKeyName()]
                  ? `${selectedOption[selectedKeyName()]}`
                  : ''
              "
              [img]="selectedOption['image']?.url ?? ''"
              borderRadius="rounded-full" />
          </label>

          <span
            [class.text-neutral-900]="
              selectedOption[selectedKeyName()] && !disabled()
            "
            [class.cursor-not-allowed]="disabled()"
            [class.text-neutral-300]="disabled()">
            {{ selectedOption[selectedKeyName()] | truncate: 11 }}
          </span>
        </div>

        <lib-chevron-down-icon
          [iconClass]="
            'size-5 ' +
            (isDropdownOpen ? 'rotate-180' : '') +
            ' ' +
            (controlHasErrors ? 'text-destructive-500' : 'text-neutral-500')
          " />
      </div>
    } @else {
      <div
        class="flex justify-between items-center w-full placeholder-container">
        <span class="text-neutral-300" [class.cursor-not-allowed]="disabled()">
          {{ placeholder() }}
        </span>

        <lib-chevron-down-icon
          [iconClass]="
            'size-5 ' +
            (isDropdownOpen ? 'rotate-180' : '') +
            ' ' +
            (controlHasErrors ? 'text-destructive-500' : 'text-neutral-500')
          " />
      </div>
    }
  </label>

  @if (isDropdownOpen) {
    <div
      class="dropdown-list start-0 absolute text-start py-2xl px-lg rounded-lg">
      <div class="text-neutral-700 font-semibold text-xs mb-sm">
        {{ searchLabel() }}
      </div>

      <div class="relative mb-lg">
        <input
          class="dropdown-search sticky top-0 p-md w-full rounded-md text-neutral-900 font-medium text-sm placeholder:text-neutral-500"
          type="text"
          [style.cursor]="
            !options()?.length && !searchTerm() ? 'not-allowed' : ''
          "
          [disabled]="!options()?.length && !searchTerm()"
          [placeholder]="searchPlaceholder()"
          (input)="onSearchTermChange($event.target)" />

        <!-- search icon -->
        <lib-search-icon
          [iconClass]="
            'size-5 text-neutral-500 absolute end-md inset-y-[27.5%]'
          " />
      </div>

      <div class="option-dropdown-container pe-sm" (scroll)="onScroll($event)">
        @if (isLoading() && !options()?.length && this.page() === 1) {
          <lib-loader [size]="'sm'" />
        } @else if (options()?.length && this.page() >= 1) {
          @for (option of options(); track option) {
            @if (option && option.label) {
              <button
                type="button"
                (click)="selectOption(option, true); isDropdownOpen = false"
                class="bg-transparent py-md border-b border-neutral-200 block w-full cursor-pointer">
                <div class="flex items-center gap-md">
                  <lib-avatar
                    [size]="avatarSize()"
                    [fullName]="option.label || ''"
                    [img]="option.image?.url ?? ''"
                    [imgDimensions]="imgDimensions()"
                    [placeholderTextClass]="
                      'text-primary-500 font-medium text-xs'
                    "
                    borderRadius="rounded-full" />

                  <span
                    class="text-xs font-medium text-neutral-900 truncate-name text-start">
                    {{ option.label }}
                  </span>
                </div>
              </button>
            }
          }

          @if (isLoading()) {
            <div class="flex justify-center">
              <lib-loader [size]="'sm'" />
            </div>
          }
        } @else {
          <div
            class="py-2xl px-lg flex flex-col items-center justify-center gap-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="57"
              height="56"
              viewBox="0 0 57 56"
              fill="none">
              <path
                opacity="0.4"
                d="M27.3327 48.9993C39.575 48.9993 49.4993 39.075 49.4993 26.8327C49.4993 14.5904 39.575 4.66602 27.3327 4.66602C15.0904 4.66602 5.16602 14.5904 5.16602 26.8327C5.16602 39.075 15.0904 48.9993 27.3327 48.9993Z"
                fill="#D1D5DB" />
              <path
                d="M50.2 51.3333C49.78 51.3333 49.36 51.17 49.0566 50.8666L44.7166 46.5266C44.0866 45.8966 44.0866 44.87 44.7166 44.2166C45.3466 43.5866 46.3733 43.5866 47.0266 44.2166L51.3666 48.5566C51.9966 49.1866 51.9966 50.2133 51.3666 50.8666C51.04 51.17 50.62 51.3333 50.2 51.3333Z"
                fill="var(--color-primary-500)" />
              <path
                d="M33.1667 29.0508H21.5C20.5433 29.0508 19.75 28.2574 19.75 27.3008C19.75 26.3441 20.5433 25.5508 21.5 25.5508H33.1667C34.1233 25.5508 34.9167 26.3441 34.9167 27.3008C34.9167 28.2574 34.1233 29.0508 33.1667 29.0508Z"
                fill="var(--color-primary-500)" />
            </svg>
            {{ noDataLabel() }}
          </div>
        }
      </div>
    </div>
  }
</div>
